import React, {useCallback, useEffect, useState} from "react";
import {Box, Collapse, Typography} from "@mui/material";
import {
    Clip,
    GroupedClips,
    Highlight,
    KeyMomentClip,
    VideoContentTypes,
    VideoUpdatableFields
} from '../../../types/clipTypes';
import ClipListElem from "../ClipListElem/ClipListElem";
import TransparentButton from "../../CommonComponents/TransparentButton/TransparentButton";
import {useTheme} from "../../../contexts/theme";
import ClipTable from "../ClipTable/clipTable";
import HighlightPopup from "../../HighlightsPopup/HighlightPopup";
import EditorPopup from "../../EditorPopup/EditorPopup";
import StyledSnackbar from "../../CommonComponents/StyledSnackbar/StyledSnackbar";
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import CustomTabPanel from "../../CommonComponents/CustomTabPanel/CustomTabPanel"
import HighlightListElem from "../HighligthListElem/HighlightListElem";
import {Event} from "../../../types/eventTypes"
import {deleteAllClips} from "../../../queries/matchPageQueries";
import {LoadError} from "../../../types/generic";
import ErrorSnackBar from "../../CommonComponents/ErrorSnackBar/ErrorSnackBar";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {formatUTCDate} from "../../../utils/dateTimeutils";
import DeleteIcon from "@mui/icons-material/Delete";
import StyledPopper from "../../CommonComponents/StyledPopper/StyledPopper";
import SortIcon from '@mui/icons-material/Sort';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import SpinnerBox from "../../CommonComponents/SpinnerBox/SpinnerBox";
import VideoJSPlayer from "../../CommonComponents/VideoJSComponent/VideoJSPlayer";
import StyledPopup from "../../CommonComponents/StyledPopup/StyledPopup";
import KeyMomentEditor from "../../KeyMomentEditorJSX/KeyMomentEditor";
import axiosInstance from "../../../queries/axiosInstance";
import {TrimPoint} from "../../../types/editor";
import {getClipByEventId, getClipFormOptions, getUrlClipForDownload} from "../../../queries/clipQueries";
import {download} from "../../../utils/utils";


interface ClipsPlaylistProps {
    groupedClips: GroupedClips | undefined;
    height?: number;
    setOutEditorOpen: (open: boolean) => void;
    event: Event | undefined
    onDeleteClip: (clip: Clip) => void;
    onDeleteAllJobClips: (jobId: string) => void;
    onDeleteAllKeyMomentClips: (keyMoment: string) => void;
    onDeleteHighlight: (highlight: Highlight) => void;
    onVideoContentUpdate: (videoContentId: string, contentType: VideoContentTypes, field: VideoUpdatableFields, value: string[] | string) => void;
    setSortAsc: (sortAscending: boolean) => void;
    sortAsc: boolean;
    clipDeleting: boolean;
    firstLoading: boolean;
    streamUrl: string;
    minRunningJobsStatus: string;
}

const ClipsPlaylist: React.FC<ClipsPlaylistProps> = ({minRunningJobsStatus, streamUrl, onVideoContentUpdate, firstLoading, clipDeleting, sortAsc, setSortAsc, onDeleteHighlight, onDeleteAllKeyMomentClips, onDeleteAllJobClips, onDeleteClip, setOutEditorOpen, groupedClips, height, event}) => {

    const theme = useTheme();

    const [moreInfoOpen, setMoreInfoOpen] = useState<string>('');
    const [isHighlightPopupOpen, setHighlightPopupOpen] = useState(false);
    const [isClipDownloading, setIsClipDownloading] = React.useState<string>('NODOWNLOAD');
    const [currentTab, setCurrentTab] = React.useState<number>(0);
    const [loadError, setLoadError] = React.useState<LoadError>({error: false, message: ""});
    const [accordionJobId, setAccordionJobId] = React.useState<string>('');
    const [popperAnchor, setPopperAnchor] = React.useState<null | HTMLElement>(null);
    const [possibleJobDelete, setPossibleJobDelete] = React.useState<string>('');
    const [possibleKeyMomentDelete, setPossibleKeyMomentDelete] = React.useState<string>('');

    const [editorClip, setEditorClip] = useState<Clip | undefined | Highlight>(undefined);
    const [editorOpen, setEditorOpen] = useState<boolean>(false)
    const [playerOpen, setPlayerOpen] = React.useState<boolean>(false)
    const [editorContentType, setEditorContentType] = React.useState<'clip' | 'highlight'>('clip');

    const [isEventEditorOpen, setEventEditorOpen] = useState<boolean>(false);

    const [highlightCreationState, setHighlightCreationState] = React.useState<'Success' | 'Start' | 'Failed' | undefined> ();

    const iconStyle = {
        color: theme.palette.white,
        fontSize: theme.fontSizes.title
    }

    function getMainClip(keyMomentClip: KeyMomentClip) {
        const ris = keyMomentClip.clips.find((clip: Clip) => clip.id === keyMomentClip.main_clip);
        return ris ? ris : {}
    }
    const onClipClick = useCallback((clip: Clip) => {
        if (!clip){
            setLoadError({error: true, message: 'The clip is corrupted and cannot be edited'});
        }else{
            setEditorClip(JSON.parse(JSON.stringify(clip)));
            setEditorContentType("clip");
            setOutEditorOpen(true);
            setEditorOpen(true);
        }
    }, [])
    const onImgClipClick = useCallback((clip: Clip) => {
        if (!clip){
            setLoadError({error: true, message: 'The clip is corrupted and cannot be edited'});
        }else{
            setEditorClip(JSON.parse(JSON.stringify(clip)));
            setEditorContentType("clip");
            setOutEditorOpen(true);
            setEditorOpen(true);
        }
    }, [])

    const onHighlightClick = useCallback((highlight: Highlight) => {
        if (!highlight){
            setLoadError({error: true, message: 'The clip is corrupted and cannot be edited'});
        }else{
            setEditorClip(JSON.parse(JSON.stringify(highlight)));
            setEditorContentType('highlight');
            if (highlight.aspect_ratio && !(highlight.aspect_ratio >= 16/9 - 0.01 && highlight.aspect_ratio <= 16/9 + 0.01)){
                setPlayerOpen(true);
            }else{
                setOutEditorOpen(true);
                setEditorOpen(true);
            }
        }
    }, [])
    const onHighlightImgClick = useCallback((highlight: Highlight) => {
        if (!highlight){
            setLoadError({error: true, message: 'The clip is corrupted and cannot be edited'});
        }else{
            setEditorClip(JSON.parse(JSON.stringify(highlight)));
            setEditorContentType("highlight");
            if (highlight.aspect_ratio && !(highlight.aspect_ratio >= 16/9 - 0.01 && highlight.aspect_ratio <= 16/9 + 0.01)){
                setPlayerOpen(true);
            }else{
                setOutEditorOpen(true);
                setEditorOpen(true);
            }
        }
    }, [])

    const onClipUpdate = useCallback((clipId: any, field: any, value: any) => {
        if (!clipId || !field || !value){
            setLoadError({error: true, message: 'The clip is corrupted and cannot be updated'});
        }
        onVideoContentUpdate(clipId, 'clip', field, value)
    },[])

    const handleKeyMomentDeletion = useCallback((event: any, keyMomentClip: string) => {
        setPopperAnchor(event.currentTarget);
        setPossibleKeyMomentDelete(keyMomentClip);
    },[])

    const setMoreClipById = useCallback((clipId: string) => {
        clipId === moreInfoOpen ? setMoreInfoOpen('') : setMoreInfoOpen(clipId);
    }, [groupedClips, moreInfoOpen])

    function closeEditor(){
        setOutEditorOpen(false);
        setEditorOpen(false);
    }

    function closeHighlightPopup(){
        setHighlightPopupOpen(false);
        setOutEditorOpen(false);
    }

    function closeEventEditor(){
        setEventEditorOpen(false)
        setOutEditorOpen(false);
    }

    function openHighlightPopup(){
        //setHighlightGroupedClips(JSON.parse(JSON.stringify(groupedClips)));
        setHighlightPopupOpen(true);
        setOutEditorOpen(true);
    }

    function a11yProps(index: number) {
        return {
            id: `simple-tab-${index}`,
            'aria-controls': `simple-tabpanel-${index}`,
        };
    }

    function handleClipTableSelection(clip: Clip){
        if (!clip){
            setLoadError({error: true, message: 'The clip is corrupted and cannot be edited'});
        }else{
            setEditorClip(JSON.parse(JSON.stringify(clip)));
            setOutEditorOpen(true);
            setEditorOpen(true);
        }
    }

    const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
        setCurrentTab(newValue);
    };

    useEffect(() => {
        if (isClipDownloading === 'FAILED'){
            setEditorOpen(false)
        }
    }, [isClipDownloading]);

    useEffect(() => {
        if (highlightCreationState !== undefined){
            if(highlightCreationState === 'Start'){
                setLoadError({error: true, message: 'Highlight will be created shortly'})
            }else if (highlightCreationState === 'Failed'){
                setLoadError({error: true, message: 'Something went wrong while creating highlight'})
            }else if (highlightCreationState === 'Success'){
                setLoadError({error: true, message: 'Highlight created successfully'})
            }
        }
    }, [highlightCreationState]);

    function handleDeleteAllClips(){
        try{
            if (event){
                deleteAllClips(event.id)
            }else{
                setLoadError({error: true, message: 'Problem retrieving event infos'});
            }
        }catch (error){
            setLoadError({error: true, message: 'Error deleting all clips'});
        }
    }

    function handleCreateClip(){
        setEventEditorOpen(true)
        setOutEditorOpen(true);
    }

    return (
        <>
            <Box display={'flex'} justifyContent={'space-between'}>
                <Tabs
                    value={currentTab}
                    onChange={handleTabChange}
                    aria-label="playlist tabs"
                    sx={{
                        '& .MuiTab-root': { color: theme.palette.white, fontSize: theme.fontSizes.title, fontWeight: theme.fontWeights.title, paddingTop: 0, paddingBottom: 0},
                        '& .Mui-selected': { color: theme.palette.orange + ' !important', fontWeight: theme.fontWeights.title },
                        '& .MuiTabs-indicator': { backgroundColor: theme.palette.orange }
                    }}
                >
                    <Tab label="Clips" {...a11yProps(0)} />
                    <Tab label="Jobs" {...a11yProps(1)} />
                    <Tab label="Highlights" {...a11yProps(2)} />
                </Tabs>
                <Box onClick={() => setSortAsc(!sortAsc)} sx={{marginTop: theme.spacing.sm, cursor: 'pointer'}}>
                    <SortIcon sx={{color: theme.palette.white}}/>
                    {sortAsc ? <ArrowDownwardIcon sx={iconStyle} /> : <ArrowUpwardIcon sx={iconStyle}/>}
                </Box>
            </Box>

            <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                color: 'silver',
                paddingTop: theme.spacing.sm
            }}>
                <Box sx={{
                    height: height ? height + 'px' : '300px',
                    marginBottom: theme.spacing.none,
                    display: 'flex',
                    flexDirection: 'column'
                }}>
                    <Box sx={{overflowY: 'auto', flex: 1, overflowX: 'hidden'}}>
                        {firstLoading && <SpinnerBox height={'50vh'} text={currentTab === 2 ? 'Retrieving Highlights' : 'Retrieving Clips'}/>}
                            <CustomTabPanel value={currentTab} index={0}>
                                {groupedClips && groupedClips.keyMomentGroups.map((keyMomentClip, index) => (
                                    <Box key={'playlistClip' + index}>
                                        <ClipListElem
                                            displayMoreInfoIcon={true}
                                            setMoreInfoClip={setMoreClipById}
                                            clip={(getMainClip(keyMomentClip) as Clip)}
                                            moreInfoOpen={keyMomentClip.main_clip === moreInfoOpen}
                                            noMarginTop={index === 0}
                                            onClipImageClick={onImgClipClick}
                                            onClipTitleClick={onClipClick}
                                            onClipDeleteClick={onDeleteClip}
                                            onDeleteAllKeyMomentClips= {handleKeyMomentDeletion}
                                            onClipUpdate={onClipUpdate}
                                            clipDeleting = {clipDeleting}
                                        />
                                        <Collapse in={keyMomentClip.main_clip === moreInfoOpen}>
                                            <ClipTable mainClip={keyMomentClip.main_clip} clips={keyMomentClip.clips} onClipClick={handleClipTableSelection} onClipDeleteClick={onDeleteClip}></ClipTable>
                                        </Collapse>
                                    </Box>
                                ))
                                }
                        </CustomTabPanel>

                        <CustomTabPanel value={currentTab} index={2}>
                            {groupedClips && groupedClips?.highlights.map((highlight, index) => (
                                <Box sx={{marginTop: index !== 0 ? theme.spacing.md : 0 }}>
                                    <HighlightListElem
                                        highlight={highlight}
                                        onHighlightImageClick={onHighlightImgClick}
                                        onHighlightTitleClick={onHighlightClick}
                                        onHighlightDeleteClick={onDeleteHighlight}
                                        onHighlightContentUpdate={(highlightId, field, value) => onVideoContentUpdate(highlightId, 'highlight', field, value)}
                                        clipDeleting = {clipDeleting}
                                    />
                                </Box>
                            ))}
                        </CustomTabPanel>

                        <CustomTabPanel value={currentTab} index={1}>
                            {groupedClips && groupedClips?.jobGroups.map((jobGroup, index) => (
                                <Accordion key={jobGroup.id + index}
                                    expanded={jobGroup.id === accordionJobId}
                                    onChange={() => {jobGroup.id === accordionJobId ? setAccordionJobId('') : setAccordionJobId(jobGroup.id)}}
                                    sx={{
                                        backgroundColor: jobGroup.id === accordionJobId ? 'transparent' : theme.palette.orange,
                                        color: theme.palette.white,
                                        marginTop: index!==0 ? theme.spacing.sm : 0,
                                        "& .MuiAccordionSummary-content.Mui-expanded": {
                                            marginBottom: 0,
                                            marginTop: 0,
                                        },
                                        "& .MuiButtonBase-root-MuiAccordionSummary-root.Mui-expanded": {
                                            minHeight: 'auto',
                                        },
                                    }}
                                >
                                    <AccordionSummary
                                        expandIcon={<ExpandMoreIcon sx={{color: theme.palette.white}}/>}
                                        aria-controls="panel1-content"
                                        id="panel1-header"
                                    >
                                        <DeleteIcon
                                            fontSize={'small'}
                                            sx={{marginRight: theme.spacing.sm, color: theme.palette.white}}
                                            onClick={(event: any) => {
                                                event.stopPropagation()
                                                setPopperAnchor(popperAnchor ? null : event.currentTarget);
                                                setPossibleJobDelete(jobGroup.id);
                                            }}/>
                                        <Typography component="span">{formatUTCDate(jobGroup.extraction_schedule, true, true)}</Typography>

                                    </AccordionSummary>
                                    <AccordionDetails sx={{color: theme.palette.lightGrey}}>
                                        {jobGroup.clips.map(clip => (
                                            <ClipListElem
                                                displayMoreInfoIcon={false}
                                                setMoreInfoClip={setMoreClipById}
                                                clip={(clip)}
                                                moreInfoOpen={false}
                                                noMarginTop={index !== 0}
                                                onClipImageClick = {onImgClipClick}
                                                onClipTitleClick = {onClipClick}
                                                onClipDeleteClick = {onDeleteClip}
                                                onClipUpdate={onClipUpdate}
                                                clipDeleting = {clipDeleting}
                                            />
                                        ))}
                                    </AccordionDetails>
                                </Accordion>
                            ))}
                        </CustomTabPanel>
                    </Box>
                    {height && <Box sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            height: '50px',
                            paddingTop: theme.spacing.md,
                            paddingBottom: theme.spacing.md
                        }}>
                            <TransparentButton
                                text='Create Highlight'
                                width={'150px'}
                                onClick={openHighlightPopup}
                                disabled={groupedClips && groupedClips.keyMomentGroups.length === 0}/>
                            <TransparentButton
                                text='Create Clip'
                                width={'150px'}
                                onClick={handleCreateClip}
                                disabled={!event || !event.id || !streamUrl}
                            />
                            <TransparentButton
                                text='Delete All Clips'
                                width={'150px'}
                                onClick={handleDeleteAllClips}
                                disabled={!event || !event.id || (groupedClips && groupedClips.keyMomentGroups.length === 0)}
                            />
                        </Box>}
                </Box>
            </Box>

            <HighlightPopup
                setCreationLoading={setHighlightCreationState}
                isOpen={isHighlightPopupOpen}
                handleClose={closeHighlightPopup}
                clips={groupedClips ? groupedClips.clips : []}
            />
            <StyledPopup
                isOpen={playerOpen}
                onClose={() => setPlayerOpen(false)}
                height="92vh"
                width="calc((87vh/9)*16)"
            >
                <VideoJSPlayer url={editorClip && editorClip.src ? editorClip?.src : ''} sourceType={'video/mp4'}/>
            </StyledPopup>

            <StyledPopup isOpen={isEventEditorOpen} onClose={closeEventEditor} height={"100vh"} width="calc((100vh/9)*16)" title={""}>
                {event != undefined && (
                    <KeyMomentEditor
                        loadClips={(_: any, eventId:string) => getClipByEventId(eventId)}
                        loadFormOptions={(_: any) => getClipFormOptions()}
                        onClipComputation={(_: any, jobId: string, trimPoint: TrimPoint) => getUrlClipForDownload(jobId, trimPoint)}
                        onDownload={download}
                        event={event}
                        axiosInstance={axiosInstance}
                        live = {minRunningJobsStatus === 'Running'}
                        dispose={!isEventEditorOpen}
                    />
                )}
            </StyledPopup>


            <EditorPopup
                setExternalError={setLoadError}
                clip={editorClip}
                isOpen={editorOpen}
                handleClose={closeEditor}
                setIsClipDownloading={setIsClipDownloading}
                contentType={editorContentType}
            />
            <StyledSnackbar
                open={isClipDownloading === 'DOWNLOAD'}
                setClose={() => setIsClipDownloading('NODOWNLOAD')}
                message={'Wait for your download'}
                severity={'info'}
                closeButton={true}
            />
            <StyledSnackbar
                open={isClipDownloading === 'FAILED'}
                setClose={() => setIsClipDownloading('NODOWNLOAD')}
                message={'Error to download your clip'}
                severity={'error'}
                closeButton={true}
            />
            <ErrorSnackBar autoHideDuration={5000} open={loadError.error} setClose={() => setLoadError({message:'', error: false})} errorMessage={loadError.message}/>
            <StyledPopper
                message={'Are you sure you want to delete all the job clips'}
                open={Boolean(possibleJobDelete)}
                anchorEl={popperAnchor}
                id={'Delete-Clips-Jobs-Dialog'}
                afterResponse={async (message) => {
                    if (message === 'YES') {
                        onDeleteAllJobClips(possibleJobDelete);
                    }
                    setPopperAnchor(null);
                    setPossibleJobDelete('');
                }}/>
            <StyledPopper
                message={'Are you sure you want to delete all Key Moment Clips?'}
                open={Boolean(possibleKeyMomentDelete)}
                anchorEl={popperAnchor}
                id={'Delete-Clips-Key-Moment-Dialog'}
                afterResponse={async (message) => {
                    if (message === 'YES') {
                        onDeleteAllKeyMomentClips(possibleKeyMomentDelete);
                    }
                    setPopperAnchor(null);
                    setPossibleKeyMomentDelete('')
                }}/>
        </>
    );
};

export default ClipsPlaylist;